import { useCallback, useState } from "react";
import type { IrrigationPlanExecutionReportParams } from "@/api/model/irrigation-plan-execution-report";
import type { AUTPropertyIrrigationPlanStep } from "@/api/queries/account";
import { apiService } from "@/api/service";
import { dayjs, dayjsTZ, minDate } from "@/utils/date";

import { DEFAULT_DATE_RANGE_DAYS, DEFAULT_REPORT_LIMIT } from "../constants";
import type { ReportFilters, ReportItem } from "../types";

interface UseIrrigationReportOptions {
  project?: {
    irrigation_plans?: Array<{
      id: string;
      name: string;
    }>;
  } | null;
  onSuccess?: (hasData: boolean) => void;
  onError?: (error: string) => void;
}

interface UseIrrigationReportReturn {
  reportData: ReportItem[];
  loading: boolean;
  error: string | null;
  fetchReport: (filters: ReportFilters) => Promise<void>;
}

/**
 * Custom hook for fetching and managing irrigation plan execution report data
 */
export function useIrrigationReport(options: UseIrrigationReportOptions = {}): UseIrrigationReportReturn {
  const { project, onSuccess, onError } = options;

  const [reportData, setReportData] = useState<ReportItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchReport = useCallback(
    async (filters: ReportFilters) => {
      if (!filters.projectId) {
        const errorMsg = "Selecione um projeto";
        setError(errorMsg);
        onError?.(errorMsg);
        return;
      }

      if (!filters.startDate || !filters.endDate) {
        const errorMsg = "Selecione as datas de início e fim";
        setError(errorMsg);
        onError?.(errorMsg);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const irrigationReportParams: IrrigationPlanExecutionReportParams = {
          project_id: filters.projectId,
          plan_ids: filters.planIds.length > 0 ? filters.planIds : undefined,
          start_date: dayjsTZ(filters.startDate).startOf("day").toDate(),
          end_date: minDate(dayjsTZ(filters.endDate).endOf("day").toDate(), new Date()).toDate(),
          order: "desc",
          limit: DEFAULT_REPORT_LIMIT,
        };

        const response =
          await apiService.irrigationPlanExecutionReport.readIrrigationPlanExecutionReport(irrigationReportParams);

        // Enrich data with plan names and sector names for all steps
        const enrichedData: ReportItem[] = response.map((item): ReportItem => {
          const plan = project?.irrigation_plans?.find((p) => p.id === item.irrigation_plan);
          const snapshotPlan = item.snapshot?.plan;
          const snapshotSteps: AUTPropertyIrrigationPlanStep[] = snapshotPlan?.steps ?? [];
          const snapshotSectors = snapshotPlan.project?.sectors ?? [];

          const getStepSectorName = (stepId: string): string => {
            const step = snapshotSteps.find((s) => s.id === stepId);
            if (!step) return "Setor Desconhecido";
            const sector = snapshotSectors.find((sec) => sec.id === step.sector);
            return sector?.name ?? "Setor Desconhecido";
          };

          // Map activated irrigation steps to sector names
          const activatedIrrigationSectorNames = item.activated_steps.map(getStepSectorName);

          // Map missed irrigation steps to sector names
          const missedIrrigationSectorNames = item.missed_irrigation_steps.map(getStepSectorName);

          // Map activated fertigation steps to sector names
          const activatedFertigationSectorNames = item.activated_ferti_steps.map(getStepSectorName);

          // Map missed fertigation steps to sector names
          const missedFertigationSectorNames = item.missed_fertigation_steps.map(getStepSectorName);

          return {
            ...item,
            planName: plan?.name ?? "Plano não encontrado",
            // Add the mapped sector names as additional properties
            activatedIrrigationSectors: activatedIrrigationSectorNames,
            missedIrrigationSectors: missedIrrigationSectorNames,
            activatedFertigationSectors: activatedFertigationSectorNames,
            missedFertigationSectors: missedFertigationSectorNames,
          };
        });

        setReportData(enrichedData);
        onSuccess?.(enrichedData.length > 0);
      } catch (err) {
        const errorMsg = "Erro ao buscar dados do relatório. Tente novamente.";
        setError(errorMsg);
        onError?.(errorMsg);
      } finally {
        setLoading(false);
      }
    },
    [project, onSuccess, onError]
  );

  return {
    reportData,
    loading,
    error,
    fetchReport,
  };
}

/**
 * Get default filters for the report
 */
export function getDefaultFilters(searchParams?: URLSearchParams): ReportFilters {
  const today = dayjs();
  const startDate = today.subtract(DEFAULT_DATE_RANGE_DAYS, "day");

  return {
    projectId: searchParams?.get("projectId") ?? "",
    planIds: searchParams?.get("planId") ? [searchParams.get("planId")!] : [],
    startDate: startDate.format("YYYY-MM-DD"),
    endDate: today.format("YYYY-MM-DD"),
  };
}
